import './styles.css'
import { ApiError, type CurrentUser, type MonthView, api } from './api.js'
import { addMonths, currentMonth } from './format.js'
import { renderBills, renderBudget, renderIncome, renderLog, renderPayroll } from './panels/lists.js'
import { renderPlan } from './panels/plan.js'
import { renderPurchases } from './panels/purchases.js'
import { renderHeader, renderInvestigateChip, renderSummary } from './panels/summary.js'
import { closeReport, openReport, stepReportMonth } from './report.js'
import { openEditor, openSignIn } from './forms.js'
import { isSetupUrl, openSetup } from './setup.js'

/**
 * Application entry point.
 *
 * The prototype kept every figure in memory and recalculated in the browser.
 * Here the server owns the numbers: this fetches one view model per month and
 * lays it out. That is what makes Amy's phone, Michael's laptop and the PDF
 * sent to the accountant agree.
 */

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

let month = currentMonth()
let view: MonthView | null = null
let user: CurrentUser | null = null
let logFilter = 'All'

const canWrite = () => user?.permissions.includes('data.write') ?? false
const canEdit = () => canWrite() && !(view?.closed ?? false)

export async function refresh(): Promise<void> {
  view = await api.month(month)
  render()
}

function render(): void {
  if (!view) return

  renderHeader(view, canWrite(), {
    canClose: user?.permissions.includes('month.close') ?? false,
    canReopen: user?.permissions.includes('month.reopen') ?? false,
  })
  renderInvestigateChip(view)
  renderSummary(view)
  renderPlan(view, canWrite())
  renderBills(view, canEdit())
  renderPayroll(view, canEdit())
  renderIncome(view, canEdit())
  renderBudget(view, canEdit())
  renderPurchases(view, canEdit())
  renderLog(view, canEdit(), logFilter)
}

async function goToMonth(next: string): Promise<void> {
  month = next
  await refresh()
}

/** Saves a plan or month setting, then reloads so every figure stays consistent. */
async function saveSetting(body: Record<string, unknown>, scope: 'month' | 'plan'): Promise<void> {
  try {
    if (scope === 'month') await api.put(`/api/months/${month}/settings`, body)
    else await api.put('/api/plan-settings', body)
    await refresh()
  } catch (error) {
    reportError(error)
  }
}

function reportError(error: unknown): void {
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Something went wrong.'
  window.alert(message)
}

/** Debounces the settings inputs so typing does not fire a save per keystroke. */
function debounce<T extends unknown[]>(fn: (...args: T) => void, delay = 600) {
  let timer: ReturnType<typeof setTimeout> | undefined
  return (...args: T) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
}

function wireEvents(): void {
  $('#prev').addEventListener('click', () => void goToMonth(addMonths(month, -1)))
  $('#next').addEventListener('click', () => void goToMonth(addMonths(month, 1)))

  $('#invChip').addEventListener('click', () => {
    logFilter = logFilter === 'Investigate' ? 'All' : 'Investigate'
    render()
    $('#logPanel').scrollIntoView({ behavior: 'smooth', block: 'start' })
  })

  $('#reportBtn').addEventListener('click', () => openReport(month))
  $('#rptClose').addEventListener('click', closeReport)
  $('#rptPrev').addEventListener('click', () => stepReportMonth(-1))
  $('#rptNext').addEventListener('click', () => stepReportMonth(1))

  for (const kind of ['pdf', 'xlsx', 'csv'] as const) {
    const button = document.querySelector<HTMLElement>(`#rpt${kind[0]!.toUpperCase()}${kind.slice(1)}`)
    button?.addEventListener('click', () => {
      // A plain navigation, so the browser handles the download and the
      // session cookie authenticates it.
      window.location.href = `/api/months/${month}/report.${kind}`
    })
  }

  // Plan and month settings.
  const savePeriods = debounce((value: string) => {
    const periods = Number(value)
    void saveSetting({ payPeriods: value === '' ? null : periods }, 'month')
  })
  $('#p-periods').addEventListener('input', (event) =>
    savePeriods((event.target as HTMLInputElement).value),
  )

  $('#p-basis').addEventListener('change', (event) => {
    const chosen = (event.target as HTMLSelectElement).value
    void saveSetting({ basis: chosen === 'budget' ? 'budget_payroll' : chosen }, 'plan')
  })

  const saveCustom = debounce((value: string) =>
    void saveSetting({ customAmount: value || '0' }, 'plan'),
  )
  $('#p-custom').addEventListener('input', (event) =>
    saveCustom((event.target as HTMLInputElement).value),
  )

  const saveFee = debounce((value: string) =>
    void saveSetting({ cardFeePct: Number(value || '0') }, 'plan'),
  )
  $('#p-fee').addEventListener('input', (event) =>
    saveFee((event.target as HTMLInputElement).value),
  )

  const saveCushion = debounce((value: string) =>
    void saveSetting({ cushionStart: value || '0' }, 'month'),
  )
  $('#p-res').addEventListener('input', (event) =>
    saveCushion((event.target as HTMLInputElement).value),
  )

  const saveRmr = debounce((value: string) =>
    void saveSetting({ recurringMonthlyRevenue: value === '' ? null : value }, 'month'),
  )
  const rmrInput = document.querySelector<HTMLInputElement>('#p-rrm')
  rmrInput?.addEventListener('input', (event) =>
    saveRmr((event.target as HTMLInputElement).value),
  )

  $('#p-alerts').addEventListener('change', (event) =>
    void saveSetting(
      { alertsEnabled: (event.target as HTMLSelectElement).value === 'on' },
      'plan',
    ),
  )
  $('#p-alertday').addEventListener('change', (event) =>
    void saveSetting({ alertWeekday: Number((event.target as HTMLSelectElement).value) }, 'plan'),
  )
  const saveAlertDays = debounce((value: string) => {
    const days = Number(value)
    if (days >= 1 && days <= 31) void saveSetting({ alertLeadDays: days }, 'plan')
  })
  $('#p-alertdays').addEventListener('input', (event) =>
    saveAlertDays((event.target as HTMLInputElement).value),
  )

  // Delegated clicks: bill checkboxes, bucket changes, edit buttons, filters.
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement

    if (target.closest('#clearEx')) {
      // Not undoable, so it asks for the word rather than a yes/no.
      const typed = window.prompt(
        'This permanently removes every record marked "sample". Your own entries ' +
          'and the weekly charge settings stay.\n\nType DELETE to confirm.',
      )
      if (typed === 'DELETE') {
        void api
          .post('/api/sample-data/clear', { confirm: 'DELETE' })
          .then(refresh)
          .catch(reportError)
      }
      return
    }

    if (target.closest('#closeMonth')) {
      if (
        window.confirm(
          `Close ${month}? The report is frozen exactly as it stands now, and the ` +
            'month becomes read-only until an owner reopens it.',
        )
      ) {
        void api.post(`/api/months/${month}/close`).then(refresh).catch(reportError)
      }
      return
    }

    if (target.closest('#reopenMonth')) {
      const reason = window.prompt('Why is this month being reopened? This is recorded.')
      if (reason && reason.trim().length >= 3) {
        void api.post(`/api/months/${month}/reopen`, { reason }).then(refresh).catch(reportError)
      }
      return
    }

    const useExpected = target.closest<HTMLElement>('#useExpected')
    if (useExpected) {
      const total = useExpected.dataset['total']
      if (total) void saveSetting({ recurringMonthlyRevenue: total }, 'month')
      return
    }

    const filter = target.closest<HTMLElement>('[data-filter]')
    if (filter) {
      logFilter = filter.dataset['filter'] ?? 'All'
      render()
      return
    }

    const edit = target.closest<HTMLElement>('[data-edit]')
    if (edit && view) {
      const [store, id] = (edit.dataset['edit'] ?? '').split(':')
      openEditor(store ?? '', id ?? '', view, refresh)
      return
    }

    const add = target.closest<HTMLElement>('[data-add]')
    if (add && view) {
      openEditor(add.dataset['add'] ?? '', '', view, refresh)
      return
    }

    const amazon = target.closest<HTMLElement>('[data-amazon]')
    if (amazon && view) {
      openEditor('entries', amazon.dataset['amazon'] ?? '', view, refresh)
      return
    }

    const deposit = target.closest<HTMLElement>('[data-deposit]')
    if (deposit && view) {
      openEditor('entries', '', view, refresh, {
        kind: 'income',
        occurredOn: deposit.dataset['deposit'] ?? '',
        revenueType: 'Recurring',
        paymentMethod: 'Card',
      })
    }
  })

  document.addEventListener('change', (event) => {
    const target = event.target as HTMLElement

    const paid = target.closest<HTMLInputElement>('[data-paid]')
    if (paid) {
      void api
        .put(`/api/bills/${paid.dataset['paid']}/paid/${month}`, { paid: paid.checked })
        .then(refresh)
        .catch((error: unknown) => {
          reportError(error)
          void refresh()
        })
      return
    }

    const bucket = target.closest<HTMLSelectElement>('[data-bucket]')
    if (bucket) {
      void api
        .put(`/api/transactions/${bucket.dataset['bucket']}`, { bucket: bucket.value })
        .then(refresh)
        .catch((error: unknown) => {
          reportError(error)
          void refresh()
        })
    }
  })
}

async function start(): Promise<void> {
  // The one-time setup link lands here. It must be handled before anything
  // asks /api/auth/me, because the person following it has no account set up
  // yet — showing them the sign-in form would ask for a password that does
  // not exist, which is exactly what it used to do.
  if (isSetupUrl()) {
    openSetup()
    return
  }

  try {
    user = await api.me()
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // Not signed in: the sign-in panel takes over and calls start() again.
      openSignIn(() => void start())
      return
    }
    throw error
  }

  wireEvents()
  await refresh()
}

void start().catch((error: unknown) => {
  document.body.innerHTML =
    '<main style="padding:2rem;font-family:system-ui"><h1>NI Money Manager</h1>' +
    '<p>Could not reach the server. Try reloading in a moment.</p></main>'
  console.error(error)
})
